import { createDryMassEntryOptionSelector } from 'Components/Worksheets/MoistureContentWorksheet';
import { actions } from 'Data/ReduxViewModels/WorksheetGrid';
import { hasNonEmptyValue } from 'Lib/Any';
import { DateTimeDataFormat } from 'Lib/Format';
import { useHasChanged } from 'Lib/Hooks';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import moment from 'moment';
import { pick } from 'ramda';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateTimeInput, Field, TextInput, TextOutput } from '../../Core';
import { withInjectedProps } from '../../Core/InjectedPropsContext';
import { FieldSet } from '../../Layout';
import { LaboratoryDropdown, StandaloneEquipmentDropdownField } from '../../List';
import { WorksheetGridRefreshButton, createFirstWorksheetSelector } from '../Core';
import WorksheetGridAdditionalDataInjectedPropsProvider from '../Core/WorksheetGridAdditionalDataInjectedPropsProvider';
import { WorksheetGridCommonDataInjectedPropsProvider } from '../Core/WorksheetGridCommonDataInjectedPropsProvider';
import { WorksheetGridQueryInjectedPropsProvider } from '../Core/WorksheetGridQueryInjectedPropsProvider';
import '../WorksheetGridControls.scss';

export const DryMassGridBalanceKey = 'DryMassEntry-Balance';
export const DryMassGridOvenKey = 'Oven-DryMass';

let DryMassWorksheetGridControls: React.FC = () => {
  const laboratoryId = useSelector((state) => state.worksheetGrid.query.params.LaboratoryId);
  const homeLaboratoryId = useSelector((state) => state.user.data.HomeLaboratory.Id);
  const dryMassBalanceSelected = useSelector((state) =>
    hasNonEmptyValue(state.worksheetGrid.commonData['Measurement.Balance'])
  );
  const defaultLaboratoryBalance = useSelector(
    (state) => state.worksheetGrid.additionalData['LaboratoryDefaultBalance'],
    deepEquals
  );
  const dryMassBalanceReadability = useSelector(
    pipe(
      (state) => (state.equipment.equipmentData as any)[DryMassGridBalanceKey]?.data,
      (data) => pick(['Readability', 'Uom'], data ?? {}) as any
    )
  );
  const dateTimeEntered = useSelector((state) =>
    hasNonEmptyValue(state.worksheetGrid.commonData['Measurement.DateTimeRemovedFromOvenLocal'])
  );

  /**
   * Currently all worksheets in the grid must come from the same laboratory
   * We assume that the laboratory data attached to the first worksheet applies to all worksheets.
   * (In future, we should fetch laboratory data independently from the worksheet model
   * so we have this whether or not a test is returned)
   */
  const showConstantDryMassEntryFields = useSelector(
    pipe(createDryMassEntryOptionSelector(createFirstWorksheetSelector), (o) => o !== 'Never')
  );
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(actions.cleanup());
    };
  }, [dispatch]);

  // Clear equipment when lab changes
  const hasLabChanged = useHasChanged(laboratoryId);
  useEffect(() => {
    if (hasLabChanged) {
      dispatch(
        actions.setCommonDataValue({
          key: 'Measurement.Balance',
          value: undefined,
          sync: true,
        })
      );
      dispatch(actions.setAdditionalDataValue({ key: 'SampleIdFilter', value: undefined }));
      dispatch(actions.setQueryValue({ name: 'OvenId', value: undefined })); //Clear OvenID filter when labs change
      dispatch(actions.getDefaultBalance({ laboratoryId: laboratoryId }));
    }
  }, [dispatch, hasLabChanged, laboratoryId]);

  const hasDefaultLaboratoryBalanceChanged = useHasChanged(defaultLaboratoryBalance);
  const hasDefaultBalance = hasNonEmptyValue(defaultLaboratoryBalance);

  useEffect(() => {
    if ((hasDefaultLaboratoryBalanceChanged && !dryMassBalanceSelected) || hasDefaultBalance) {
      dispatch(
        actions.setCommonDataValue({
          key: 'Measurement.Balance',
          value: defaultLaboratoryBalance,
          sync: true,
        })
      );
    }
  }, [
    dispatch,
    hasDefaultLaboratoryBalanceChanged,
    dryMassBalanceSelected,
    defaultLaboratoryBalance,
    hasDefaultBalance,
  ]);

  useEffect(() => {
    if (!hasDefaultBalance) {
      dispatch(
        actions.setCommonDataValue({
          key: 'Measurement.Balance',
          value: null,
          sync: true,
        })
      );
    }
  }, [dispatch, hasDefaultBalance]);
  // Set the date removed from oven as current time by default
  // Called on load or when this field is added/removed as different labs may or may not require this to be entered.
  useEffect(() => {
    dispatch(
      actions.setCommonDataValue({
        key: 'Measurement.DateTimeRemovedFromOvenLocal',
        value: showConstantDryMassEntryFields
          ? moment().local().seconds(0).milliseconds(0).format('YYYY-MM-DDTHH:mm:ss.SSS')
          : null,
        sync: true,
      })
    );
  }, [dispatch, showConstantDryMassEntryFields]);

  return (
    <>
      <div className="WorksheetGridControls">
        <div className="WorksheetGridControls-Query">
          <div className="WorksheetGridControls-HeaderFieldGroup">
            <WorksheetGridQueryInjectedPropsProvider>
              <Field
                label="Laboratory"
                input={<LaboratoryDropdown />}
                name="LaboratoryId"
                simpleValue
                isClearable={false}
                autoSelectSingleOption
                searchImmediately
                autoSelectedValue={homeLaboratoryId}
                horizontal
              />
              <StandaloneEquipmentDropdownField
                label="Oven"
                equipmentType="oven"
                name="OvenId"
                id={DryMassGridOvenKey}
                simpleValue
                laboratoryId={laboratoryId}
                disabled={!laboratoryId}
                horizontal
              />
            </WorksheetGridQueryInjectedPropsProvider>
            <WorksheetGridAdditionalDataInjectedPropsProvider>
              <Field horizontal input={<TextInput />} label="Filter by Sample ID" name="SampleIdFilter" />
            </WorksheetGridAdditionalDataInjectedPropsProvider>
          </div>
          <div className="WorksheetGridControls-RefreshButton">
            <WorksheetGridRefreshButton />
          </div>
        </div>
        <div className="WorksheetGridControls-CommonData">
          <div className="WorksheetGridControls-HeaderFieldGroup">
            <WorksheetGridCommonDataInjectedPropsProvider>
              <FieldSet horizontalLabelWidth="medium">
                <StandaloneEquipmentDropdownField
                  label="Balance"
                  equipmentType="balance"
                  id={DryMassGridBalanceKey}
                  name="Measurement.Balance"
                  laboratoryId={laboratoryId}
                  disabled={!laboratoryId}
                  horizontal
                  messages={
                    laboratoryId && !dryMassBalanceSelected
                      ? [
                          {
                            severity: 'info',
                            message: 'A balance must be selected before dry masses can be entered.',
                          },
                        ]
                      : undefined
                  }
                />
                {dryMassBalanceReadability &&
                  dryMassBalanceReadability.Uom &&
                  dryMassBalanceReadability.Readability && (
                    <Field
                      input={<TextOutput />}
                      horizontal
                      label={`Readable to Nearest (${dryMassBalanceReadability.Uom})`}
                      value={dryMassBalanceReadability?.Readability}
                    />
                  )}
              </FieldSet>
              {showConstantDryMassEntryFields && (
                <Field
                  horizontal
                  label="Time Out of Oven"
                  input={<DateTimeInput />}
                  name="Measurement.DateTimeRemovedFromOvenLocal"
                  dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime}
                  disabled={!laboratoryId}
                  messages={
                    laboratoryId && !dateTimeEntered
                      ? [
                          {
                            severity: 'info',
                            message: 'Date/time out of oven must be entered before dry masses can be entered.',
                          },
                        ]
                      : undefined
                  }
                />
              )}
            </WorksheetGridCommonDataInjectedPropsProvider>
          </div>
        </div>
      </div>
    </>
  );
};

DryMassWorksheetGridControls = withInjectedProps(DryMassWorksheetGridControls);

export default DryMassWorksheetGridControls;
export { DryMassWorksheetGridControls };
